// Writing an operation (Guide §10.2 / §10.7): validate → allocate deviceSeq → apply to the local mirror → append to the outbox, all in ONE
// IndexedDB transaction. Nothing the user did can be half-saved, and nothing waits for the network.
import { OP_SCHEMAS, bizDate, formatInvoiceNo, formatReturnNo, newId, newLinkToken, type OpPayload, type OpPayloadInput, type OpType } from '@dd/shared';
import { db, metaGet, metaSet } from '@/db';
import type { OutboxItem } from '@/db/types';
import { APP_VERSION } from '@/lib/env';
import { nowIso } from '@/lib/clock';
import { ApplyCtx } from './ctx';
import { APPLY } from './apply';
import { recomputeOverlay } from './overlay';
import { syncContext } from './context';
import { refreshCounts } from './counts';

type Kick = (delayMs?: number) => void;
let kickFn: Kick = () => undefined;
export const registerKick = (k: Kick): void => { kickFn = k; };

export interface EnqueueOpts {
  entityId?: string;
  changedFields?: string[];
  baseVersion?: number;
  overrideToken?: string;
  label?: string;
}

/** Parse + normalise a payload with the shared Zod schema (defaults filled, numbers → strings). Throws ZodError on bad input. */
export function parsePayload<T extends OpType>(type: T, input: OpPayloadInput<T>): OpPayload<T> {
  return OP_SCHEMAS[type].parse(input) as OpPayload<T>;
}

export async function enqueue<T extends OpType>(type: T, input: OpPayloadInput<T>, opts: EnqueueOpts = {}): Promise<OutboxItem> {
  const c = syncContext();
  const payload = parsePayload(type, input);
  const entityId = opts.entityId ?? newId();
  const d = db();
  const clientTs = nowIso();
  const item = await d.transaction('rw', d.tables, async () => {
    const seq = ((await metaGet<number>('opSeq')) ?? Math.floor(Date.now() / 1000)) + 1;
    await metaSet('opSeq', seq);
    const opId = newId();
    const biz = bizDate(clientTs);
    const ax = new ApplyCtx(d, { opId, actor: c.actor, clientTs, shopId: c.shopId }, biz, c.role);
    const op: OutboxItem = {
      opId, deviceSeq: seq, shopId: c.shopId, type, entityId, payload, baseVersion: opts.baseVersion, changedFields: opts.changedFields, clientTs, actor: c.actor,
      overrideToken: opts.overrideToken, appVersion: APP_VERSION, state: 'pending', tries: 0, undo: [], localRows: [], createdAt: Date.now(), label: opts.label,
    };
    await (APPLY[type] as (c: ApplyCtx, p: OpPayload<T>, o: OutboxItem) => Promise<void>)(ax, payload, op);
    op.undo = ax.undo;
    op.localRows = ax.local;
    await d.outbox.add(op);
    await recomputeOverlay();
    return op;
  });
  await refreshCounts();
  kickFn();
  return item;
}

// ── numbering (Bible 2.x: A-000123 per device; returns R-A-000045) ─────────────────────────────
const MAX_ACROSS = 99_999_999;

/** Next invoice number for THIS device: max(counter, highest seen on this device code) + 1. */
export async function reserveInvoiceNo(): Promise<{ invoiceNo: string; deviceCode: string; deviceSeq: number }> {
  const c = syncContext();
  const code = c.deviceCode;
  if (!code) throw new Error('device_code_missing');
  const d = db();
  return d.transaction('rw', d.sales, d.meta, async () => {
    const stored = (await metaGet<number>('invoiceSeq')) ?? 0;
    const mine = await d.sales.where('invoiceNo').startsWith(`${code}-`).toArray();
    const seen = mine.reduce((m, s) => (s.deviceCode === code ? Math.max(m, s.deviceSeq) : m), 0);
    const next = Math.min(Math.max(stored, seen) + 1, MAX_ACROSS);
    await metaSet('invoiceSeq', next);
    return { invoiceNo: formatInvoiceNo(code, next), deviceCode: code, deviceSeq: next };
  });
}

export async function reserveReturnNo(): Promise<string> {
  const c = syncContext();
  const code = c.deviceCode;
  if (!code) throw new Error('device_code_missing');
  const d = db();
  return d.transaction('rw', d.sale_returns, d.meta, async () => {
    const stored = (await metaGet<number>('returnSeq')) ?? 0;
    const re = new RegExp(`^R-${code}-(\\d+)$`);
    let seen = 0;
    await d.sale_returns.each((r) => { const m = re.exec(r.returnNo); if (m) seen = Math.max(seen, Number(m[1])); });
    const next = Math.max(stored, seen) + 1;
    await metaSet('returnSeq', next);
    return formatReturnNo(code, next);
  });
}

export const newToken = newLinkToken;

// ── undo / dismiss ───────────────────────────────────────────────────────────
/** Roll an operation back locally (rejected by the server, or the user dismissed a failed one). */
export async function revertOp(opId: string, remove: boolean): Promise<void> {
  const d = db();
  await d.transaction('rw', d.tables, async () => {
    const op = await d.outbox.get(opId);
    if (!op) return;
    for (const u of [...op.undo].reverse()) {
      if (u.before) await d.table(u.table).put(u.before);
      else await d.table(u.table).delete(u.id);
    }
    for (const l of op.localRows) await d.table(l.table).delete(l.id);
    if (remove) await d.outbox.delete(opId);
    else await d.outbox.update(opId, { undo: [], localRows: [] });
    await recomputeOverlay();
  });
  await refreshCounts();
}

export type { OpPayloadInput };
